// Reproducción (celo, servicio, diagnóstico, parto). Ganadería → Reproducción. Fase 2A (667).
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Heart, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import {
  Animal, Campo, METODOS_REPRO, MetodoRepro, Modal, RESULTADOS_DX, RESULTADOS_PARTO, ReproCalendarioItem,
  ReproEvento, ReproKpis, SEXOS, SexoAnimal, SortTh, TIPOS_REPRO, TipoRepro, camposRepro, fieldCls, ganRpc,
  hoyISO, invalidateGanaderia, metodoReproLabel, num, tipoReproLabel, useSort,
} from './shared';

type SubTab = 'calendario' | 'kpis' | 'eventos';

export function Reproduccion() {
  const [tab, setTab] = useState<SubTab>('calendario');
  const [modalAbierto, setModalAbierto] = useState(false);
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const admin = isAdmin();

  const tabBtn = (t: SubTab, label: string) => (
    <button
      className={`rounded-md px-3 py-1.5 text-sm ${tab === t ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`}
      onClick={() => setTab(t)}
    >
      {label}
    </button>
  );

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Heart className="w-5 h-5" /> Reproducción</h1>
      <div className="flex flex-wrap gap-1 border-b pb-2">
        {tabBtn('calendario', 'Calendario')}
        {tabBtn('kpis', 'KPIs')}
        {tabBtn('eventos', 'Eventos')}
      </div>

      {tab === 'calendario' && <CalendarioTab />}
      {tab === 'kpis' && <KpisTab />}
      {tab === 'eventos' && <EventosTab admin={admin} onRegistrar={() => setModalAbierto(true)} />}

      {modalAbierto && (
        <RegistrarReproModal onClose={() => setModalAbierto(false)} onSaved={() => invalidateGanaderia(qc)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Calendario
function CalendarioTab() {
  const { data = [], isFetching } = useQuery<ReproCalendarioItem[]>({
    queryKey: ['gan-repro-calendario'], queryFn: () => ganRpc<ReproCalendarioItem[]>('gan_repro_calendario'),
    staleTime: 0, refetchOnMount: 'always',
  });

  const getVal = useCallback((r: ReproCalendarioItem, key: string): string | number | null => {
    switch (key) {
      case 'arete': return r.arete;
      case 'nombre': return r.nombre;
      case 'evento_sugerido': return r.evento_sugerido;
      case 'fecha_sugerida': return r.fecha_sugerida;
      case 'dias': return r.dias;
      case 'estado': return r.estado;
      default: return null;
    }
  }, []);
  const sort = useSort(data, getVal, 'fecha_sugerida', 'asc');
  const vencidos = data.filter((d) => d.estado === 'vencido').length;

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Calendario reproductivo ({data.length}) — {vencidos} vencido(s)</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3 text-right">N°</th>
            <SortTh col="arete" sort={sort}>Arete</SortTh>
            <SortTh col="nombre" sort={sort}>Nombre</SortTh>
            <SortTh col="evento_sugerido" sort={sort}>Evento sugerido</SortTh>
            <SortTh col="fecha_sugerida" sort={sort}>Fecha sugerida</SortTh>
            <SortTh col="dias" sort={sort} align="right">Días</SortTh>
            <SortTh col="estado" sort={sort}>Estado</SortTh>
          </tr></thead>
          <tbody>
            {sort.sorted.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin eventos pendientes en el calendario reproductivo</td></tr>}
            {sort.sorted.map((r, i) => {
              const vencido = r.estado === 'vencido';
              return (
                <tr key={`${r.hembra_id}-${i}`} className={`border-b last:border-0 ${vencido ? 'bg-urgente-soft/40' : 'bg-pendiente-soft/30'}`}>
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium">{r.arete}</td>
                  <td className="py-2 pr-3">{r.nombre ?? '—'}</td>
                  <td className="py-2 pr-3">{r.evento_sugerido}</td>
                  <td className="py-2 pr-3">{r.fecha_sugerida}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.dias}</td>
                  <td className="py-2 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${vencido ? 'bg-urgente-soft text-urgente' : 'bg-pendiente-soft text-pendiente'}`}>
                      {vencido ? 'Vencido' : 'Próximo'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// --------------------------------------------------------------------- KPIs
function KpisTab() {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const { data, isFetching } = useQuery<ReproKpis>({
    queryKey: ['gan-repro-kpis', desde, hasta],
    queryFn: () => ganRpc<ReproKpis>('gan_repro_kpis', { _desde: desde || null, _hasta: hasta || null }),
    staleTime: 0, refetchOnMount: 'always',
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <Campo label="Desde" className="w-40"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta" className="w-40"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </CardContent>
      </Card>

      {!data ? (
        <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Tasa de preñez" value={data.tasa_prenez != null ? `${num(data.tasa_prenez, 1)}%` : '—'} />
          <Kpi label="Servicios / preñez" value={data.servicios_por_prenez != null ? num(data.servicios_por_prenez, 2) : '—'} />
          <Kpi label="IEP (días)" value={data.iep_dias != null ? num(data.iep_dias, 0) : '—'} />
          <Kpi label="Días abiertos" value={data.dias_abiertos != null ? num(data.dias_abiertos, 0) : '—'} />
          <Kpi label="Total servicios" value={String(data.total_servicios)} />
          <Kpi label="Diagnósticos" value={String(data.total_dx)} />
          <Kpi label="Preñadas" value={String(data.prenadas)} />
          <Kpi label="Total partos" value={String(data.total_partos)} />
          <Kpi label="Abortos" value={String(data.total_abortos)} />
          <Kpi label="Nacimientos" value={String(data.total_nacimientos)} />
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold font-mono tabular-nums mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ Eventos
function EventosTab({ admin, onRegistrar }: { admin: boolean; onRegistrar: () => void }) {
  const [hembraId, setHembraId] = useState('');
  const { data: animales = [] } = useQuery<Animal[]>({ queryKey: ['gan-animales-todos'], queryFn: () => ganRpc<Animal[]>('gan_animal_listar') });
  const hembras = animales.filter((a) => a.sexo === 'H');

  const { data = [], isFetching } = useQuery<ReproEvento[]>({
    queryKey: ['gan-repro-eventos', hembraId],
    queryFn: () => ganRpc<ReproEvento[]>('gan_repro_evento_listar', { _hembra: hembraId || null }),
  });

  const getVal = useCallback((r: ReproEvento, key: string): string | number | null => {
    switch (key) {
      case 'fecha': return r.fecha;
      case 'hembra_arete': return r.hembra_arete;
      case 'hembra_nombre': return r.hembra_nombre;
      case 'tipo': return tipoReproLabel(r.tipo);
      case 'metodo': return metodoReproLabel(r.metodo);
      case 'resultado': return r.resultado;
      case 'costo': return r.costo;
      default: return null;
    }
  }, []);
  const sort = useSort(data, getVal);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <Campo label="Hembra" className="w-64">
            <select className={fieldCls} value={hembraId} onChange={(e) => setHembraId(e.target.value)}>
              <option value="">Todas</option>
              {hembras.map((a) => <option key={a.id} value={a.id}>{a.arete}{a.nombre ? ` · ${a.nombre}` : ''}</option>)}
            </select>
          </Campo>
          <Button className="ml-auto" disabled={!admin} onClick={onRegistrar}><Plus className="w-4 h-4 mr-1" /> Registrar evento</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Eventos reproductivos ({data.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="fecha" sort={sort}>Fecha</SortTh>
              <SortTh col="hembra_arete" sort={sort}>Hembra</SortTh>
              <SortTh col="tipo" sort={sort}>Tipo</SortTh>
              <SortTh col="metodo" sort={sort}>Método</SortTh>
              <th className="py-2 pr-3">Toro/semental</th>
              <SortTh col="resultado" sort={sort}>Resultado</SortTh>
              <th className="py-2 pr-3">Cría</th>
              <th className="py-2 pr-3 text-right">Días gest.</th>
              <SortTh col="costo" sort={sort} align="right">Costo</SortTh>
              <th className="py-2 pr-3">Observaciones</th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">Sin eventos registrados</td></tr>}
              {sort.sorted.map((e, i) => (
                <tr key={e.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{e.fecha}</td>
                  <td className="py-2 pr-3 font-medium">{e.hembra_arete}{e.hembra_nombre ? ` · ${e.hembra_nombre}` : ''}</td>
                  <td className="py-2 pr-3">{tipoReproLabel(e.tipo)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{metodoReproLabel(e.metodo)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.toro_arete ?? e.semental ?? '—'}</td>
                  <td className="py-2 pr-3">{e.resultado ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.cria_arete ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{e.dias_gestacion ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{e.costo != null ? num(e.costo, 2) : '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.observaciones ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// -------------------------------------------------- Registrar evento (modal)
function RegistrarReproModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [hembraId, setHembraId] = useState('');
  const [tipo, setTipo] = useState<TipoRepro>('celo');
  const [metodo, setMetodo] = useState<MetodoRepro>('monta');
  const [toroId, setToroId] = useState('');
  const [semental, setSemental] = useState('');
  const [resultado, setResultado] = useState('');
  const [diasGestacion, setDiasGestacion] = useState('');
  const [costo, setCosto] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [fecha, setFecha] = useState(hoyISO());
  const [crearCria, setCrearCria] = useState(false);
  const [criaArete, setCriaArete] = useState('');
  const [criaNombre, setCriaNombre] = useState('');
  const [sexoCria, setSexoCria] = useState<SexoAnimal>('H');

  const campos = camposRepro(tipo);

  const { data: animales = [] } = useQuery<Animal[]>({ queryKey: ['gan-animales-todos'], queryFn: () => ganRpc<Animal[]>('gan_animal_listar') });
  const hembras = animales.filter((a) => a.sexo === 'H');
  const toros = animales.filter((a) => a.sexo === 'M');

  const registrar = useMutation({
    mutationFn: () => ganRpc('gan_repro_registrar', {
      _data: {
        hembra_id: hembraId, tipo,
        metodo: campos.metodo ? metodo : null,
        toro_id: campos.toro && toroId ? toroId : null,
        semental: campos.toro && !toroId ? (semental || null) : null,
        resultado: (campos.resultadoDx || campos.resultadoParto) ? (resultado || null) : null,
        dias_gestacion: campos.diasGestacion && diasGestacion !== '' ? Number(diasGestacion) : null,
        costo: costo === '' ? null : Number(costo),
        observaciones: observaciones || null, fecha,
        crear_cria: campos.cria && crearCria ? 'true' : undefined,
        cria_arete: campos.cria && crearCria ? (criaArete || null) : undefined,
        cria_nombre: campos.cria && crearCria ? (criaNombre || null) : undefined,
        sexo_cria: campos.cria && crearCria ? sexoCria : undefined,
      },
    }),
    onSuccess: () => { toast.success('Evento reproductivo registrado'); onSaved(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puedeGuardar = !!hembraId
    && (!campos.resultadoDx || !!resultado)
    && (!campos.resultadoParto || !!resultado)
    && (!(campos.cria && crearCria) || !!criaArete.trim());

  return (
    <Modal title="Registrar evento reproductivo" onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Campo label="Hembra *">
            <select className={fieldCls} value={hembraId} onChange={(e) => setHembraId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {hembras.map((a) => <option key={a.id} value={a.id}>{a.arete}{a.nombre ? ` · ${a.nombre}` : ''}</option>)}
            </select>
          </Campo>
          <Campo label="Tipo *">
            <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value as TipoRepro)}>
              {TIPOS_REPRO.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Campo>
          <Campo label="Fecha *"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
        </div>

        {campos.metodo && (
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Método">
              <select className={fieldCls} value={metodo} onChange={(e) => setMetodo(e.target.value as MetodoRepro)}>
                {METODOS_REPRO.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
              </select>
            </Campo>
            {campos.toro && (
              <Campo label="Toro (o semental por nombre)">
                <select className={fieldCls} value={toroId} onChange={(e) => { setToroId(e.target.value); if (e.target.value) setSemental(''); }}>
                  <option value="">— Selecciona un toro registrado —</option>
                  {toros.map((t) => <option key={t.id} value={t.id}>{t.arete}{t.nombre ? ` · ${t.nombre}` : ''}</option>)}
                </select>
              </Campo>
            )}
          </div>
        )}
        {campos.toro && !toroId && (
          <Campo label="O nombre del semental (si es de IA / no está en el hato)">
            <input className={fieldCls} value={semental} onChange={(e) => setSemental(e.target.value)} />
          </Campo>
        )}

        {campos.resultadoDx && (
          <Campo label="Resultado *">
            <select className={fieldCls} value={resultado} onChange={(e) => setResultado(e.target.value)}>
              <option value="">— Selecciona —</option>
              {RESULTADOS_DX.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </Campo>
        )}
        {campos.resultadoParto && (
          <Campo label="Resultado *">
            <select className={fieldCls} value={resultado} onChange={(e) => setResultado(e.target.value)}>
              <option value="">— Selecciona —</option>
              {RESULTADOS_PARTO.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </Campo>
        )}

        {campos.diasGestacion && (
          <Campo label="Días de gestación"><input type="number" min="0" className={fieldCls} value={diasGestacion} onChange={(e) => setDiasGestacion(e.target.value)} /></Campo>
        )}

        {campos.cria && (
          <div className="rounded-md border p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm select-none cursor-pointer">
              <input type="checkbox" className="h-4 w-4 rounded border-input" checked={crearCria} onChange={(e) => setCrearCria(e.target.checked)} />
              Dar de alta la cría en el hato
            </label>
            {crearCria && (
              <div className="grid grid-cols-3 gap-3">
                <Campo label="Arete de la cría *"><input className={fieldCls} value={criaArete} onChange={(e) => setCriaArete(e.target.value)} /></Campo>
                <Campo label="Nombre"><input className={fieldCls} value={criaNombre} onChange={(e) => setCriaNombre(e.target.value)} /></Campo>
                <Campo label="Sexo *">
                  <select className={fieldCls} value={sexoCria} onChange={(e) => setSexoCria(e.target.value as SexoAnimal)}>
                    {SEXOS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </Campo>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Campo label="Costo ($)"><input type="number" min="0" step="0.01" className={fieldCls} value={costo} onChange={(e) => setCosto(e.target.value)} /></Campo>
        </div>
        <Campo label="Observaciones"><textarea className={`${fieldCls} h-16`} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></Campo>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={registrar.isPending} disabled={!puedeGuardar} onClick={() => registrar.mutate()}>Registrar</Button>
        </div>
      </div>
    </Modal>
  );
}
